import React, { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import "moment/locale/sl";
import { Card, Col, Container, Row, Pagination, Table } from "react-bootstrap";
import { useTable, usePagination, useSortBy, TableOptions } from "react-table";
import axios from "../../utils/axios";
import useAuth from "../../hooks/useAuth";
import Spinner from "react-bootstrap/Spinner";
import config from "../../config";
import { useParams } from "react-router-dom";

const PaginationTable = (props: TableOptions<Record<string, unknown>>) => {
  const { columns, data, isLoading, totalRows, fetchData } = props;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultCanSort: true,
      manualPagination: true,
      autoResetPage: false,
      pageCount: Math.ceil(totalRows / 50),
      initialState: {
        pageIndex: 0,
        pageSize: 50,
        sortBy: [],
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (totalRows === 0 && gotoPage) {
      gotoPage(0);
    }
  }, [totalRows, gotoPage]);

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  return (
    <Card>
      <Card.Body className="table-responsive">
        <Table striped hover {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {!page.length && isLoading ? (
              <tr>
                <td colSpan={10} className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Nalagam...</span>
                  </Spinner>
                </td>
              </tr>
            ) : !page.length ? (
              <tr>
                <td colSpan={10} className="text-center">
                  Ni podatkov...
                </td>
              </tr>
            ) : (
              <tr></tr>
            )}
            {page.map((row: any, i: any) => {
              prepareRow(row);
              return (
                <tr
                  className="fadeIn"
                  {...row.getRowProps()}
                  onClick={() => {
                    const win = window.open(row.original.url, "_blank");
                    if (win !== null) {
                      win.focus();
                    }
                  }}
                >
                  {row.cells.map((cell: any) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>

        <Row className="mt-3">
          <Col md="6">
            <span className="mx-2">
              Stran{" "}
              <strong>
                {pageIndex + 1} od {pageCount}
              </strong>
            </span>
          </Col>
          <Col md="6">
            <Pagination className="float-end">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const AdsList = () => {
  moment().locale("sl");
  const [tableData, setTableData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [title, setTitle] = useState("Oglasi");
  const fetchIdRef = useRef(0);
  const { isAuthenticated } = useAuth();
  let { listId } = useParams();
  const currencyFormatter = new Intl.NumberFormat("sl-SI", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

  const numberFormatter = new Intl.NumberFormat("sl-SI", {
    maximumFractionDigits: 0,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (listId === "2") {
      setTitle("Oglasi tovornih vozil");
    } else if (listId === "3") {
      setTitle("Oglasi delovnih strojev");
    } else if (listId === "4") {
      setTitle("Oglasi osebnih vozil - Mobile");
    } else {
      setTitle("Oglasi osebnih vozil");
    }

    if ("Notification" in window && Notification?.permission !== "granted") {
      Notification.requestPermission()
        .then((result) => {
          console.log(result);
        })
        .catch((e) => {
          console.error(e);
        });
    }

    let eventSource: any;
    if (config.NTFY_ADS_TOPIC) {
      eventSource = new EventSource(
        "https://ntfy.novioglasi.com:8080/" +
          config.NTFY_ADS_TOPIC +
          "-" +
          listId +
          "/sse?auth=" +
          config.NTFY_AUTH
      );

      console.log("subscribed");
      eventSource.onmessage = (e: any) => {
        console.log("got new ad");

        try {
          const data = JSON.parse(e.data);
          const ad = JSON.parse(data.message);

          setTableData((current: any) => {
            return [ad, ...current];
          });
          if (Notification?.permission !== "granted") {
            return;
          }

          setTimeout(() => {
            axios.get("/alerts/ad/" + ad.id + "/check").then((result) => {
              ad.alert = result.data?.alert;
              if (ad.alert) {
                const text = `Dodan je bil nov oglas: ${[
                  ad.make,
                  ad.model,
                  ad.title,
                ].join(" ")}`;

                const notification = new Notification("Novi Oglasi", {
                  body: text,
                });

                notification.onclick = (event) => {
                  event.preventDefault();
                  window.open(ad.url, "_blank");
                };
              }
            });
          }, 5000);
        } catch (e) {
          console.error(e);
        }
      };
    }

    return () => {
      eventSource && eventSource.close();
    };
  }, [listId, isAuthenticated]);

  let tableColumns = [
    {
      listIds: [1, 2, 3, 4],
      Header: "Znamka",
      accessor: "make",
    },
    {
      listIds: [1, 4],
      Header: "Model",
      accessor: "model",
    },
    {
      listIds: [1, 2, 3, 4],
      Header: "Opis",
      accessor: (d: any) => {
        if (!d.title) {
          return d.model;
        }

        return d.title;
      },
    },
    {
      listIds: [3],
      Header: "Kategorija",
      accessor: "category",
    },
    {
      listIds: [1, 2, 3, 4],
      Header: "Letnik",
      accessor: "year",
    },
    {
      listIds: [1, 2, 4],
      Header: "Kilometri",
      accessor: (d: any) => {
        if (!d.kilometers) {
          return "/";
        }

        return numberFormatter.format(d.kilometers);
      },
    },
    {
      listIds: [3],
      Header: "Ure",
      accessor: (d: any) => {
        if (!d.kilometers) {
          return "/";
        }

        return numberFormatter.format(d.kilometers);
      },
    },
    {
      listIds: [1, 4],
      Header: "KM",
      accessor: "KM",
    },
    {
      listIds: [1, 2, 3, 4],
      Header: "Cena",
      accessor: (d: any) => {
        if (!d.latestPrice) {
          return "/";
        }

        return currencyFormatter.format(d.latestPrice);
      },
    },
    {
      listIds: [1, 2, 3, 4],
      Header: "Trgovec",
      accessor: (d: any) => {
        if (d.ownerURL == null) {
          return "ne";
        }

        return "da";
      },
    },
    {
      listIds: [1, 2, 3, 4],
      Header: "Dodan",
      accessor: (d: any) => {
        return moment(d.createdAt).fromNow();
      },
      sortType: (a: any, b: any) => {
        if (a.values.createdAt > b.values.createdAt) {
          return 1;
        }

        if (a.values.createdAt < b.values.createdAt) {
          return -1;
        }
        return 0;
      },
    },
  ];

  tableColumns = tableColumns.filter((col) =>
    col.listIds.includes(parseInt(listId || "0"))
  );

  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     return;
  //   }
  //   // TODO: hack for backend
  //   // setTableData([]);
  //   setIsLoading(true);
  //   axios
  //     .get("/lists/" + listId)
  //     .then((response) => {
  //       setTableData(response.data?.ads?.rows);
  //       setTotalRows(response.data?.ads?.count);
  //       setIsLoading(false);
  //     })
  //     .catch(() => setIsLoading(false));
  // }, [isAuthenticated, listId]);

  const fetchData = useCallback(
    ({ pageSize, pageIndex }) => {
      if (!isAuthenticated) {
        return;
      }

      const fetchId = ++fetchIdRef.current;

      setIsLoading(true);

      axios
        .get(
          "/lists/" + listId + "?page=" + pageIndex + "&pageSize=" + pageSize
        )
        .then((response) => {
          if (fetchId === fetchIdRef.current) {
            setTableData(response.data?.ads?.rows);
            setTotalRows(response.data?.ads?.count);
            setIsLoading(false);
            window.scrollTo(0, 0);
          }
        })
        .catch((e) => {
          console.error(e);
          setIsLoading(false);
        });
    },
    [isAuthenticated, listId]
  );

  useEffect(() => {
    if (listId) {
      setTableData([]);
      setTotalRows(0);
    }
  }, [listId]);

  return (
    <React.Fragment>
      <Helmet title="Oglasi" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{title}</h1>

        <PaginationTable
          data={tableData}
          totalRows={totalRows}
          isLoading={isLoading}
          columns={tableColumns}
          fetchData={fetchData}
        />
      </Container>
    </React.Fragment>
  );
};

export default AdsList;
